import React from 'react';
import { BreakpointsInPx } from '../../../enums/BreakpointsInPx';
import CInputNumeric from '../../../components/CInputNumeric';
import type { IResponseTableOrPlot } from '../../../models/IResponseTableOrPlot';
import type { TPandasTable } from '../../../models/TPandasTable';
import { useAppSelector } from '../../../hooks/hooks';
import type { IOptionsCalculatorConfig } from '../../../models/IOptionsCalculatorConfig';
import CButton from '../../../components/CButton';
import { Replay } from '@mui/icons-material';
import CLocalizedTextString from '../../../components/CLocalizedTextString';
import { type IRequestState } from '../../../models/IRequestState';

interface CCalculatorParametersProps {
  optionsTable: IResponseTableOrPlot<TPandasTable>;
  optionsConfigInput: IOptionsCalculatorConfig;
  handleInputChange: (input: Partial<IOptionsCalculatorConfig>) => void;
  handleValidChange: (input: Partial<IOptionsCalculatorConfig>) => void;
  onReset: () => void;
  enableReset: boolean;
  tableState: IRequestState;
}

export default function CCalculatorParameters(props: CCalculatorParametersProps) {
  const {dashboardOutletDimensions} = useAppSelector((state) => state.dashboard);

  const stockPriceLabel = CLocalizedTextString('topics.calculator.s');
  const optionStrikeLabel = CLocalizedTextString('topics.calculator.k');
  const volatilityLabel = CLocalizedTextString('topics.calculator.v');
  const timeToMaturityLabel = CLocalizedTextString('topics.calculator.t');
  const riskFreeRateLabel = CLocalizedTextString('topics.calculator.r');
  const dividendYieldLabel = CLocalizedTextString('topics.calculator.q');

  const deleteButton= (
    <CButton icons={{start: <Replay/>}}
             onClick={props.onReset}
             disabled={!props.enableReset || props.tableState.isLoading}
             isForm={true}
             style={{borderWidth: 1}} />);

  return (
    <>
      {
        (!props.optionsTable.state.isError || !!props.optionsTable.state) &&
        <div className={'flex flex-col gap-2'}>
          <div className={'flex gap-2'}>
            <div className={'grid gap-2'}
                 style={{ gridTemplateColumns: `repeat(${dashboardOutletDimensions.w <= BreakpointsInPx.BREAKPOINT_2 ? 1 : 2}, minmax(0, 1fr))` }}>
              <div className={'flex gap-2'}>
                <div className={'grid gap-2 grid-cols-3'}>
                  <CInputNumeric label={stockPriceLabel} geq={0} value={props.optionsConfigInput.S}
                                 onInputChange={(val) => {
                                   props.handleInputChange({ S: val });
                                 }}
                                 onValidChange={(val) => {
                                   props.handleValidChange({S: val});
                                 }}/>
                  <CInputNumeric label={optionStrikeLabel} gt={0} value={props.optionsConfigInput.K}
                                 onInputChange={(val) => {
                                   props.handleInputChange({ K: val });
                                 }}
                                 onValidChange={(val) => {
                                   props.handleValidChange({K: val});
                                 }}/>
                  <CInputNumeric label={volatilityLabel} geq={0} value={props.optionsConfigInput.v}
                                 onInputChange={(val) => {
                                   props.handleInputChange({ v: val });
                                 }}
                                 onValidChange={(val) => {
                                   props.handleValidChange({v: val});
                                 }}
                                 hideArrows/>
                </div>

                {
                  dashboardOutletDimensions.w <= BreakpointsInPx.BREAKPOINT_2 && deleteButton
                }
              </div>
              <div className={'grid gap-2 grid-cols-3'}>
                <CInputNumeric label={timeToMaturityLabel} geq={0} value={props.optionsConfigInput.T}
                               onInputChange={(val) => {
                                 props.handleInputChange({ T: val });
                               }}
                               onValidChange={(val) => {
                                 props.handleValidChange({T: val});
                               }}
                               hideArrows/>
                <CInputNumeric label={riskFreeRateLabel} geq={0} value={props.optionsConfigInput.r}
                               onInputChange={(val) => {
                                 props.handleInputChange({ r: val });
                               }}
                               onValidChange={(val) => {
                                 props.handleValidChange({r: val});
                               }}
                               hideArrows/>
                <CInputNumeric label={dividendYieldLabel} geq={0} value={props.optionsConfigInput.q}
                               onInputChange={(val) => {
                                 props.handleInputChange({ q: val });
                               }}
                               onValidChange={(val) => {
                                 props.handleValidChange({q: val});
                               }}
                               hideArrows/>
              </div>
            </div>
            {
              dashboardOutletDimensions.w > BreakpointsInPx.BREAKPOINT_2 && deleteButton
            }
          </div>
        </div>
      }
    </>
  );
}
